import { Check } from 'lucide-react';

import { UpgradeButton } from '@/modules/billing/components/UpgradeButton';
import { supportMailto } from '@/modules/support/support';
import { Button } from '@/modules/ui/components/button/Button';

import { SetupPicker } from './SetupPicker';

interface AutomationUpgradePitchProps {
  /** Billing page link, including the tenant search when applicable. */
  to: string;
}

/** What automation adds to a job, in the order a hiring manager feels it. */
const automationSteps = [
  {
    title: 'Finlay reaches out and follows up',
    detail:
      'Candidates hear from you on LinkedIn, and Finlay runs the conversation.',
  },
  {
    title: 'AI screening calls',
    detail: 'Interested candidates complete a short call before your review.',
  },
];

/**
 * The Automation tab when Stigg says the workspace's plan includes neither
 * outreach nor AI pre-screening. Every current plan, Free included, grants
 * both within its limits, so this is a subscription still on an older plan
 * version: support can switch it over, and Billing stays one click away.
 * Mounted through `FreePlanGate`; other tenants get the real controls.
 */
export function AutomationUpgradePitch({ to }: AutomationUpgradePitchProps) {
  return (
    <div className="flex max-w-[880px] flex-col gap-8">
      <section className="border-primary/30 bg-secondary-150/40 flex flex-col gap-4 rounded-xl border p-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-[15px] leading-6 font-semibold tracking-[-0.01em]">
            Automation isn’t on for this workspace yet
          </h2>
          <p className="text-muted-foreground max-w-[60ch] text-[13px] leading-5">
            Every Finlay plan includes automation, up to the plan’s limits. This
            workspace’s plan doesn’t include it yet, so Finlay finds candidates
            for this job and you contact them yourself.
          </p>
        </div>
        <ul className="flex flex-col gap-2">
          {automationSteps.map((step) => (
            <li className="flex items-start gap-2 text-[13px]" key={step.title}>
              <Check
                aria-hidden
                className="text-primary-700 mt-0.5 size-3.5 shrink-0"
              />
              <span>
                <span className="font-medium">{step.title}.</span>{' '}
                <span className="text-muted-foreground">{step.detail}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild>
            <a href={supportMailto('Turn on automation')}>Contact support</a>
          </Button>
          <UpgradeButton
            label="View plans"
            source="hm-automation-pitch"
            to={to}
            variant="ghost"
          />
        </div>
      </section>
      <section className="flex flex-col gap-3">
        <h2 className="text-[13px] font-semibold text-neutral-500">
          The setups you will choose between
        </h2>
        <div
          aria-disabled
          className="pointer-events-none opacity-60 select-none"
        >
          <SetupPicker
            current={undefined}
            disabled
            onChange={() => undefined}
            value={undefined}
          />
        </div>
      </section>
    </div>
  );
}
